// CONTRACT:C2-RENDER-ENGINE.1.0
/** HALO OVERLAP DETUNE — the pure math that stops the resting map's per-marker GLOW
 *  from AMPLIFYING into an unreadable green wash (render-polish: "green-declutter").
 *
 *  WHAT: every marker draws a soft translucent halo disc behind it (apps/cop/src/host/
 *  deckBackend.ts buildHalo, HALO_PX ~ 13 screen-px). Those halos composite ADDITIVELY,
 *  so in a dense cluster (e.g. the Hormuz centre) dozens pile up into a solid green blob
 *  that drowns the iconography. This computes, per marker, the FRACTION of its halo
 *  footprint covered by its neighbours and returns an opacity MULTIPLIER that recedes as
 *  overlap grows — a buried halo dims so the crisp MIL-2525 frame on top stays emphasized.
 *  ~90% overlap -> ~10% opacity (opacity ~= 1 - overlapFraction).
 *
 *  REUSES the canonical overlap math in ./smearOverlap.ts (circle-circle intersection +
 *  PROBABILISTIC-UNION coverage), so the halo and the focus smear share ONE detune model.
 *  The ONLY addition here is a SPATIAL BUCKET so the resting picture (which can carry
 *  thousands of halos, vs the smear's <=2 focus clouds) stays O(N) instead of O(N^2):
 *
 *  COVERAGE MODEL (deterministic, monotonic — IDENTICAL result to a brute-force scan):
 *   - bucket every disc into a uniform grid of cell = 2 * maxRadius. Two discs can only
 *     overlap when their centres are < r_i + r_j <= 2*maxRadius apart, so EVERY overlapping
 *     pair lands in the same or an adjacent cell. Scanning each disc's 3x3 cell window thus
 *     finds all non-zero overlaps — the bucket prunes work, it never drops a neighbour.
 *   - per disc, aggregate neighbour coverage via the same probabilistic union as the smear
 *     (1 - prod(1 - f_j)) so coverage stays in [0,1] and is monotonic-increasing in every
 *     pairwise fraction (more / closer / bigger neighbours never RAISE opacity).
 *   - opacity = clamp(1 - coverage, floor, 1): monotonic-DECREASING in local overlap.
 *
 *  CHANNEL LAW (I13, unchanged): this scales the halo's ALPHA only (an opacity multiplier the
 *  host folds into the halo getFillColor). It NEVER touches the marker/iconography layer, never
 *  changes hue (MONO/NVG-safe — dimming an additive pile-up only ever REDUCES the wash), and
 *  NEVER changes WHAT renders — the halo is decoration behind a still-present, still-pickable
 *  marker. Same set in, same set out (one multiplier per input disc).
 *
 *  PURE: deterministic (neighbour visitation is fixed by grid coordinates + ascending index,
 *  never by Map internals), no GPU. The discs are passed in a single consistent planar unit
 *  (the host projects lng/lat to SCREEN PIXELS so the radius — a pixel quantity — matches; pan
 *  is invariant, zoom re-derives on the host's zoomend rebuild). Tested in
 *  packages/render-engine/test/haloOverlap.test.ts. */

import {
  pairwiseOverlapFraction,
  type SmearDisc,
  type SmearOverlapOptions,
} from "./smearOverlap.ts";

// Re-exported so a halo consumer (the COP deck backend) has a single import surface for the
// disc shape it feeds in — the canonical definition still lives in ./smearOverlap.ts.
export type { SmearDisc } from "./smearOverlap.ts";

const DEFAULT_FLOOR = 0.05;

function clamp(lo: number, hi: number, x: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Per-marker HALO OPACITY MULTIPLIER in [floor, 1], one per input disc (same order).
 *  Monotonic-decreasing in local overlap: a halo with no neighbours stays 1; a halo whose
 *  footprint is ~90% covered recedes to ~0.1; a fully-buried halo recedes to `floor`.
 *  PURE + total (non-finite / zero-radius / non-finite-centre discs are treated as having no
 *  footprint -> multiplier 1, and never contribute coverage to others). Bucketed for O(N):
 *  numerically equivalent to a brute-force all-pairs scan (the grid only prunes disjoint
 *  pairs, which contribute 0 anyway). */
export function haloOpacityMultipliers(
  discs: readonly SmearDisc[],
  opts: SmearOverlapOptions = {},
): number[] {
  const floor = opts.floor ?? DEFAULT_FLOOR;
  const n = discs.length;
  const out = new Array<number>(n).fill(1);
  if (n === 0) return out;

  // Cell = 2 * the GLOBAL max radius so any overlapping pair is in the same / an adjacent
  // cell regardless of which two discs (handles the alert/altitude radius spread).
  let maxR = 0;
  for (const d of discs) {
    const r = Number.isFinite(d.r) ? d.r : 0;
    if (r > maxR) maxR = r;
  }
  if (maxR <= 0) return out; // no footprints at all -> all full opacity

  const cell = 2 * maxR;
  const cellX = (d: SmearDisc): number => Math.floor(d.x / cell);
  const cellY = (d: SmearDisc): number => Math.floor(d.y / cell);

  // Bucket disc INDICES by integer grid cell. Indices are pushed in ascending order, so each
  // bucket stays sorted -> neighbour visitation order is fully determined by geometry.
  const grid = new Map<string, number[]>();
  for (let i = 0; i < n; i++) {
    const di = discs[i];
    if (!Number.isFinite(di.x) || !Number.isFinite(di.y)) continue; // can't place -> no footprint
    const key = `${cellX(di)}|${cellY(di)}`;
    const bucket = grid.get(key);
    if (bucket) bucket.push(i);
    else grid.set(key, [i]);
  }

  for (let i = 0; i < n; i++) {
    const di = discs[i];
    const ri = Number.isFinite(di.r) ? di.r : 0;
    if (ri <= 0 || !Number.isFinite(di.x) || !Number.isFinite(di.y)) {
      out[i] = 1;
      continue;
    }
    const gx = cellX(di);
    const gy = cellY(di);
    // PROBABILISTIC UNION over neighbours in the 3x3 cell window: keep = prod(1 - f_j).
    let keep = 1;
    for (let x = gx - 1; x <= gx + 1; x++) {
      for (let y = gy - 1; y <= gy + 1; y++) {
        const bucket = grid.get(`${x}|${y}`);
        if (!bucket) continue;
        for (const j of bucket) {
          if (j === i) continue;
          const f = pairwiseOverlapFraction(di, discs[j]);
          keep *= 1 - f;
        }
      }
    }
    const coverage = 1 - keep; // in [0,1], monotonic in every f_j
    out[i] = clamp(floor, 1, 1 - coverage);
  }
  return out;
}
